/**
 * @file API 调用的个人中心接口
 * @author luyinpeng
 * @date 2026-03-25
 * @description集中管理个人中心所有相关的接口，方便维护和使用
 */

import { api } from '@/lib/api-factory';
import { ApiResponse } from '../api-types';
import {
    ChangeCurrentUserPasswordParam,
    PermApprovalsApplicationParam,
    PermApprovalsListParams,
    PermApprovalsListResult,
    PermissionApplicationParam,
    PermissionApplicationResult,
    TokenCreateParam,
    TokenCreateResult,
    TokenRefreshParam,
    UpsertUsersMyProfileParam,
    UserCurrent,
    UsersProfileResult,
    LockParam,
    CheckLockParam,
    UnlockParam,
} from './types';

/**
 * 登录以获取令牌-登录以获取令牌，用于后续的调用
 * @dataParams 登录参数 TokenCreateParam
 * @returns 登录结果 TokenCreateResult
 */
export const loginApi = async (
    params: TokenCreateParam,
): Promise<ApiResponse<TokenCreateResult> | null> => {
    // 使用新的API工厂调用方式
    const response = await api.request<TokenCreateResult, TokenCreateParam>(
        'TOKEN_CREATE',
        params,
    );
    if (!response?.success) {
        // console.log(response.message || "登录失败");
        return {
            ...response,
            success: false,
            message: response?.message ? response.message : '登录失败',
        };
    } else {
        // response.result.token = response.result.accessToken;
        const currentTime = Math.floor(Date.now() / 1000); // 当前时间的 Unix 时间戳（秒）
        response.result.expiresAt = response.result.expiresIn + currentTime;
    }

    return response as ApiResponse<TokenCreateResult>;
};

/**
 * 刷新token -刷新token，用于获取新的accessToken
 * @dataParams 刷新token参数 TokenRefreshParam
 * @returns 刷新token结果 TokenCreateResult
 */
export const refreshTokenApi = async (
    params: TokenRefreshParam,
): Promise<ApiResponse<TokenCreateResult> | null> => {
    const response = await api.request<TokenCreateResult, TokenRefreshParam>(
        'TOKEN_REFRESH',
        params,
    );
    if (!response.success) {
        return {
            ...response,
            success: false,
            message: response?.message ? response.message : '获取token失败',
        };
    }

    return response as ApiResponse<TokenCreateResult>;
};

/**
 * 获取手机号验证码-获取手机号验证码，用于登录
 * @dataParams 获取验证码验证码参数 TokenCreateParam
 * @returns 无返回值
 */
export const getVerificationCodeApi = async (
    params: TokenCreateParam,
): Promise<ApiResponse<null> | null> => {
    const response = await api.request<null, TokenCreateParam>(
        'TOKEN_CREATE',
        params,
    );
    if (!response?.success) {
        return {
            ...response,
            success: false,
            message: response?.message ? response.message : '获取验证码失败',
        };
    }

    return response as ApiResponse<null>;
};

/**
 * 退出登录 logoutApi-退出登录，用于清除token
 * @dataParams 无
 * @returns 无返回值
 */
export const logoutApi = async (): Promise<ApiResponse<null> | null> => {
    const response = await api.request<null>('TOKEN_LOGOUT');
    if (!response?.success) {
        return {
            ...response,
            success: false,
            message: response?.message ? response.message : '退出登录失败',
        };
    }

    return response as ApiResponse<null>;
};

/**
 * 获取用户profile的API-获取当前登录用户的profile信息
 * @dataParams 无
 * @returns 获取用户个人信息返回值UsersProfileResult
 */

export const getUsersCurrent =
    async (): Promise<ApiResponse<UserCurrent> | null> => {
        const response = await api.request<UserCurrent, never>('USERS_CURRENT');

        if (!response.success) {
            return {
                ...response,
                success: false,
                message: response?.message
                    ? response.message
                    : '获取用户信息失败',
            };
        }

        return response as ApiResponse<UserCurrent>;
    };

/**
 * 修改登录人登录密码-修改当前登录用户的登录密码
 * @dataParams 修改密码参数 ChangeCurrentUserPasswordParam
 * @returns 无返回值
 */
export const changeCurrentUserPassword = async (
    dataParams: ChangeCurrentUserPasswordParam,
): Promise<ApiResponse<null> | null> => {
    const response = await api.request<null, ChangeCurrentUserPasswordParam>(
        'USERS_CHANGE_PASSWORD',
        dataParams,
    );

    if (!response.success) {
        return {
            ...response,
            success: false,
            message: response?.message ? response.message : '修改密码失败',
        };
    }

    return response as ApiResponse<null>;
};

/**
 *  修改当前登录人的个人信息
 * @dataParams 修改当前登录人的个人信息
 * @returns 无返回值
 */
export const upsertUsersMyProfile = async (
    dataParams: UpsertUsersMyProfileParam,
): Promise<ApiResponse<null> | null> => {
    const response = await api.request<null, UpsertUsersMyProfileParam>(
        'USERS_UPSERT_MY_PROFILE',
        dataParams,
    );

    if (!response.success) {
        return {
            ...response,
            success: false,
            message: response?.message
                ? response.message
                : '修改当前登录人的个人信息失败',
        };
    }

    return response as ApiResponse<null>;
};

/**
 * 获取当前登录人的个人资料
 * @dataParams 无
 * @returns 获取用户个人信息返回值UsersProfileResult
 */
export const getUsersMyProfile =
    async (): Promise<ApiResponse<UsersProfileResult> | null> => {
        const response = await api.request<UsersProfileResult, never>(
            'USERS_MY_PROFILE',
        );

        if (!response.success) {
            return {
                ...response,
                success: false,
                message: response?.message
                    ? response.message
                    : '获取当前登录人的个人资料失败',
            };
        }

        return response as ApiResponse<UsersProfileResult>;
    };

// ==================== 权限审批 ====================
/**
 * 获取当前用户可审批的权限申请列表
 * @dataParams 请求参数类型 PermApprovalsListParams
 * @return 返回值类型 PermApprovalsListResult
 */
export const getPermApprovalsList = async (
    dataParams: PermApprovalsListParams,
): Promise<ApiResponse<PermApprovalsListResult> | null> => {
    const response = await api.request<
        PermApprovalsListResult,
        PermApprovalsListParams
    >('PERM_APPROVALS_LIST', dataParams);

    if (!response.success) {
        return {
            ...response,
            success: false,
            message: response?.message
                ? response.message
                : '获取权限申请列表失败',
        };
    }

    return response as ApiResponse<PermApprovalsListResult>;
};

/**
 * 审批权限申请
 * @dataParams 请求参数类型 PermApprovalsApplicationParam
 * @returns 无返回值
 */
export const approvePermissionApplication = async (
    dataParams: PermApprovalsApplicationParam,
): Promise<ApiResponse<null> | null> => {
    const response = await api.request<null, PermApprovalsApplicationParam>(
        'PERM_APPROVALS_APPLICATION',
        dataParams,
    );

    if (!response.success) {
        return {
            ...response,
            success: false,
            message: response?.message ? response.message : '审批权限申请失败',
        };
    }

    return response as ApiResponse<null>;
};

// ==================== 权限申请 ====================
/**
 * 申请权限
 * @dataParams 请求参数类型 PermissionApplicationParam
 * @returns 申请权限结果 PermissionApplicationResult
 */

export const applyPermission = async (
    dataParams: PermissionApplicationParam,
): Promise<ApiResponse<PermissionApplicationResult> | null> => {
    const response = await api.request<
        PermissionApplicationResult,
        PermissionApplicationParam
    >('PERMISSION_APPLICATION', dataParams);

    if (!response.success) {
        return {
            ...response,
            success: false,
            message: response?.message ? response.message : '申请权限失败',
        };
    }

    return response as ApiResponse<PermissionApplicationResult>;
};

// ==================== 功能锁 ====================
/**
 * 加锁API
 * @dataParams 加锁参数 LockParam
 * @returns 加锁结果
 */
export const lockApi = async (
    dataParams: LockParam,
): Promise<ApiResponse<null> | null> => {
    const response = await api.request<null, LockParam>(
        'FUNCTION_LOCK_CREATE',
        dataParams,
    );

    if (!response.success) {
        return {
            ...response,
            success: false,
            message: response?.message ? response.message : '加锁失败',
        };
    }

    return response as ApiResponse<null>;
};

/**
 * 检查功能锁API
 * @dataParams 检查锁参数 CheckLockParam
 * @returns 检查锁结果
 */
export const checkLockApi = async (
    dataParams: CheckLockParam,
): Promise<ApiResponse<null> | null> => {
    const response = await api.request<null, CheckLockParam>(
        'FUNCTION_LOCK_INSPECT',
        dataParams,
    );

    if (!response.success) {
        return {
            ...response,
            success: false,
            message: response?.message ? response.message : '检查锁失败',
        };
    }

    return response as ApiResponse<null>;
};

/**
 * 解锁API
 * @dataParams 解锁参数 UnlockParam
 * @returns 解锁结果
 */
export const unlockApi = async (
    dataParams: UnlockParam,
): Promise<ApiResponse<null> | null> => {
    const response = await api.request<null, UnlockParam>(
        'FUNCTION_LOCK_LIFT',
        dataParams,
    );

    if (!response.success) {
        return {
            ...response,
            success: false,
            message: response?.message ? response.message : '解锁失败',
        };
    }

    return response as ApiResponse<null>;
};
